import {CampaignSettingsAbstractComponent} from '../campaign-settings-abstract.component';
import {
  Campaign,
  CampaignSection,
  CustomDomain,
  LifelinePhoneSetting,
  SessionType,
  SessionTypeLabels,
  User,
} from '../../_models';
import {ActivatedRoute, Router} from '@angular/router';
import {Location} from '@angular/common';
import {
  AlertService,
  CampaignDomainService,
  CampaignService,
  LoaderService,
  SideNavService,
  UserService,
} from '../../_services';
import {FormBuilder, Validators} from '@angular/forms';
import {NgxSmartModalService} from 'ngx-smart-modal';
import {Component, ViewEncapsulation} from '@angular/core';
import * as moment from 'moment';
import {takeUntil} from 'rxjs/operators';

@Component({
  templateUrl: './campaign-domains.component.html',
  styleUrls: ['../campaign-section.scss', './campaign-domains.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class CampaignDomainsComponent extends CampaignSettingsAbstractComponent {
  public section = CampaignSection.Domains;
  domain: string = '';
  activeDomainSection: SessionType = SessionType.LegacySupport;
  public customDomains = {
    [SessionType.Marketplace]: [],
    [SessionType.PaymentUpdate]: [],
    [SessionType.CustomerPortal]: [],
    [SessionType.SupportPortal]: [],
  };
  public selectedDomain: CustomDomain = null;
  sessionTypeOptions = [
    { value: SessionType.SupportPortal, label: SessionTypeLabels[SessionType.SupportPortal],
      subtitle: 'Use a custom domain for your support portal.', isSection: true},
    { value: SessionType.LegacySupport, label: SessionTypeLabels[SessionType.LegacySupport],
      subtitle: 'Use a custom domain for your legacy support portal.', isSection: false},
    { value: SessionType.Marketplace, label: SessionTypeLabels[SessionType.Marketplace],
      subtitle: 'Use a custom domain for your product showcase experience.', isSection: true},
    { value: SessionType.PaymentUpdate, label: SessionTypeLabels[SessionType.PaymentUpdate], isSection: true,
      subtitle: 'Host your payment update portal on a custom domain to reduce friction and build trust with your customers.'},
    { value: SessionType.CustomerPortal, label: SessionTypeLabels[SessionType.CustomerPortal],
      subtitle: 'Use a custom domain for your customer portal.', isSection: true},
  ];
  subdomainTypeOptions = [];

  constructor(
    protected router: Router,
    protected location: Location,
    protected route: ActivatedRoute,
    protected campaignService: CampaignService,
    protected alertService: AlertService,
    protected formBuilder: FormBuilder,
    protected modalService: NgxSmartModalService,
    protected sideNav: SideNavService,
    protected loaderService: LoaderService,
    protected domainService: CampaignDomainService,
    protected userService: UserService
  ) {
    super(router, location, route, campaignService, sideNav, alertService, loaderService);
    this.subdomainTypeOptions = this.sessionTypeOptions;
    this.subdomainTypeOptions.push(
      {value: SessionType.RetentionDemo, label: SessionTypeLabels[SessionType.RetentionDemo]}
    );
  }

  ngOnInit() {
    this.form = this.formBuilder.group({
      subdomain: [null, Validators.required],
      subdomain_type: [SessionType.LegacySupport],
    });
    super.ngOnInit();
    this.userService
      .getCurrent()
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (user: User) => {
          this.domain = user.account.domain;
        },
        (error) => {
          this.handleError(error);
        }
      );
  }

  protected getDomainListType(type: SessionType) {
    return (type === SessionType.LegacySupport) ? SessionType.SupportPortal : type;
  }

  onCampaignFetch(data: Campaign) {
    this.form.patchValue(data);
    let customDomains = data.custom_domains || [];
    customDomains.forEach((domain: CustomDomain) => {
      this.customDomains[this.getDomainListType(domain.type)].push(domain);
    });
  }

  getCustomDomains(type: SessionType) {
    return this.customDomains[this.getDomainListType(type)] || [];
  }

  getDomainPortalType(type: SessionType) {
    return SessionTypeLabels[type] || '';
  }

  getDomainCreatedDate(date: string) {
    let dateMoment = moment(date);
    return dateMoment.format('DD MMM, YYYY');
  }

  checkSubdomain(event) {
    if (event.target.value) {
      let subdomain = event.target.value.trim().toLowerCase();

      if (subdomain.length && (!this.campaign || subdomain != this.campaign.subdomain)) {
        this.campaignService
          .checkSubdomain(subdomain)
          .pipe(takeUntil(this.destroy$))
          .subscribe(
            () => {},
            (error) => {
              this.handleSubmitError(error);
            }
          );
      }
    }
  }

  private openEditDialog() {
    this.modalService.getModal('customDomainDialog').open();
  }

  create(type: SessionType, event) {
    event.stopPropagation();
    this.activeDomainSection = type;
    this.selectedDomain = {
      id: null,
      campaign: this.campaign.id,
      domain: null,
      is_verified: false,
      verified: null,
      cname_target: null,
      type: type,
      name: null,
      visual_entry_funnels: [],
      show_phone_link: LifelinePhoneSetting.Never,
      created: null,
      support_phone: null,
      is_react: false,
    };
    this.openEditDialog();
  }

  edit(domain: CustomDomain) {
    this.selectedDomain = domain;
    this.openEditDialog();
  }

  delete(domain: CustomDomain) {
    if (window.confirm('Are you sure you want to delete this domain?')) {
      this.loaderService.show();

      this.domainService
        .delete(domain.id)
        .pipe(takeUntil(this._destroy$))
        .subscribe(
          (result) => {
            const type = this.getDomainListType(domain.type);
            this.alertService.success('Deleted domain.');
            this.loaderService.hide();
            this.customDomains[type] = this.customDomains[type].filter((data) => data.id !== domain.id);
            this.updateCampaignDomains();
          },
          (error) => {
            this.handleSubmitError(error);
            this.loaderService.hide();
          }
        );
    }
  }

  onDomainUpdated(domain: CustomDomain) {
    const type = this.getDomainListType(domain.type);
    let customDomains = this.customDomains[type];
    for (let i = 0; i < customDomains.length; i++) {
      if (customDomains[i].id === domain.id) {
        customDomains[i] = domain;
        break;
      }
    }
    this.customDomains[type] = customDomains;
    this.updateCampaignDomains();
  }

  onSaveDomain(domain: CustomDomain) {
    if (this.selectedDomain && this.selectedDomain.id) {
      this.onDomainUpdated(domain);
      this.modalService.getModal('customDomainDialog').close();
      this.modalService.getModal('customDomainPhoneDialog').close();
      this.modalService.getModal('customDomainFunnelsDialog').close();
    } else {
      this.customDomains[this.getDomainListType(domain.type)].push(domain);
      this.updateCampaignDomains();
    }
  }

  updateCampaignDomains() {
    const customDomains = [].concat(...Object.values(this.customDomains));
    this.campaign.custom_domains = customDomains;
    this.campaignService.currentCampaignUpdated(this.campaign);
  }

  editVisualFunnels(domain: CustomDomain) {
    this.selectedDomain = domain;
    this.modalService.getModal('customDomainFunnelsDialog').open();
  }

  editPhone(domain: CustomDomain) {
    this.selectedDomain = domain;
    this.modalService.getModal('customDomainPhoneDialog').open();
  }

  generateLinks(domain: CustomDomain = null) {
    this.selectedDomain = domain;
    this.modalService.getModal('linksDomainDialog').open();
  }

  showDocumentation(domain: CustomDomain = null) {
    this.selectedDomain = domain;
    this.modalService.getModal('solvpathDocumentationDomainDialog').open();
  }

  toggleSection(section: SessionType) {
    this.activeDomainSection = this.activeDomainSection === section ? null : section;
  }

  isSectionOpen(section: SessionType) {
    return this.activeDomainSection === section;
  }

}
